import {
  Children,
  cloneElement,
  type ComponentProps,
  type FC,
  type PropsWithChildren,
  type ReactElement,
  type ReactNode,
  useState,
} from 'react';

import { useFormatter } from '@eneco/i18n';
import { isElement } from 'react-is';

import { contentWithDashedPaddingClass, dashedLineClass, stepListItemClass } from './StepList.css';
import { useI18nTranslations } from '../../providers/i18n';
import type { ImageProps } from '../../types';
import { Box } from '../Box/Box';
import { ButtonOverlay } from '../ButtonOverlay/ButtonOverlay';
import { Grid } from '../Grid/Grid';
import { GridItem } from '../Grid/GridItem';
import { Heading } from '../Heading/Heading';
import { Image } from '../Image/Image';
import { IndexCircle } from '../IndexCircle/IndexCircle';
import { Stack } from '../Stack/Stack';
import { TransitionHeight } from '../Transition/Transition';
import { VisuallyHidden } from '../VisuallyHidden/VisuallyHidden';

export interface StepListItemProps extends PropsWithChildren {
  description?: ReactNode;
  step?: string;
  image?: Pick<ImageProps, 'alt' | 'src' | 'sources'>;
  amountOfSteps?: number;
  isInteractive?: boolean;
  isActive?: boolean;
  setActiveStep?: (step: string) => void;
  icon?: ReactNode;
}


const Title: FC<
  PropsWithChildren<{ step?: string; isInteractive?: boolean; isActive?: boolean; visuallyHiddenText?: string }>
> = ({ children, isInteractive, isActive, step, visuallyHiddenText }) => {
  return (
    <Heading
      as="h3"
      size={isInteractive && isActive ? 'S' : '3XS'}
      color={isInteractive && !isActive ? 'textLowEmphasis' : 'textPrimary'}
      id={`step-explainer-step-${step}`}>
      {visuallyHiddenText && <VisuallyHidden>{visuallyHiddenText}</VisuallyHidden>}
      {children}
    </Heading>
  );
};

const StepImage: FC<{ image?: Pick<ImageProps, 'alt' | 'src' | 'sources'> }> = ({ image }) =>
  image?.src ? (
    <Image
      alt={image.alt}
      src={image.src}
      width="100%"
      maxHeight="100%"
      hasLazyLoad
      sources={image.sources}
      display="block"
    />
  ) : null;

const StepListItem: FC<PropsWithChildren<StepListItemProps>> = ({
  children,
  description = '',
  step,
  image,
  isActive,
  setActiveStep,
  amountOfSteps,
  isInteractive,
  icon,
}) => {
  const { format } = useFormatter();
  const { stepOf } = useI18nTranslations();

  const visuallyHiddenText = format(`${stepOf}: `, {
    step: step! /* !, as stepList sets these values dynamically */,
    total: amountOfSteps! /* !, as stepList sets these values dynamically */,
  });

  const hasImage = Boolean(image?.src);

  if (isInteractive) {
    return (
      <li className={stepListItemClass}>
        <GridItem as="div" gridColumn="1 / 2">
          <ButtonOverlay>
            <ButtonOverlay.Button
              onClick={step ? () => setActiveStep?.(step) : undefined}
              aria-label={format('Activeer stap {step} van de {total}', {
                step: step!,
                total: amountOfSteps!,
              })}>
              <IndexCircle emphasis={isActive ? 'high' : 'low'}>{step}</IndexCircle>
            </ButtonOverlay.Button>
          </ButtonOverlay>
          {amountOfSteps && Number(step) < amountOfSteps ? (
            <span className={dashedLineClass} data-testid="dashed-line" />
          ) : null}
        </GridItem>
        <GridItem as="div" gridColumn="2/-1">
          <Stack className={contentWithDashedPaddingClass} gap="2">
            <ButtonOverlay>
              <ButtonOverlay.Button
                onClick={
                  step
                    ? () => {
                        setActiveStep?.(step);
                      }
                    : undefined
                }>
                <Title
                  step={step}
                  isInteractive={isInteractive}
                  isActive={isActive}
                  visuallyHiddenText={visuallyHiddenText}>
                  {children}
                </Title>
              </ButtonOverlay.Button>
            </ButtonOverlay>
            <TransitionHeight in={isActive} hideWhenCollapsed>
              <Grid as="div" gridTemplateColumns={{ initial: '1fr', md: '24px repeat(12, 1fr)' }}>
                <GridItem as="div" gridColumn={hasImage ? { initial: '1 / -1', md: '1 / 6' } : '1 / -1'}>
                  <Box paddingY="6">{description}</Box>
                </GridItem>
                {hasImage && (
                  <GridItem as="div" order={{ initial: -1, md: 1 }} gridColumn={{ initial: '1 / -1', md: '7 / -1' }}>
                    <StepImage image={image} />
                  </GridItem>
                )}
              </Grid>
            </TransitionHeight>
          </Stack>
        </GridItem>
      </li>
    );
  }

  return (
    <li className={stepListItemClass}>
      <GridItem as="div" gridColumn="1 / 2">
        {icon || <IndexCircle emphasis="high">{step}</IndexCircle>}
        {amountOfSteps && Number(step) < amountOfSteps ? (
          <span className={dashedLineClass} data-testid="dashed-line" />
        ) : null}
      </GridItem>
      <GridItem as="div" gridColumn={'2/-1'}>
        <Stack gap="2">
          <Title step={step} isInteractive={isInteractive} isActive={isActive} visuallyHiddenText={visuallyHiddenText}>
            {children}
          </Title>
          <Stack gap="6">
            {description}
            <Stack.Item />
          </Stack>
        </Stack>
      </GridItem>
    </li>
  );
};

type CompoundStepListItemProps = {
  Item: typeof StepListItem;
};

export const StepList: FC<
  PropsWithChildren<{ variant?: 'default' | 'interactive'; columnGap?: ComponentProps<typeof Grid>['columnGap'] }>
> &
  CompoundStepListItemProps = ({
  variant = 'default',
  columnGap = {
    initial: '2',
    md: '3',
    lg: '4',
    xl: '6',
  },
  children,
}) => {
  const arrayChildren = Children.toArray(children);
  const [activeStepState, setActiveStepState] = useState<number | undefined>(
    variant === 'interactive' ? 1 : undefined,
  );

  const setActiveStep = (step: string) => {
    setActiveStepState(Number.parseInt(step, 10));
  };

  return (
    <Grid
      as="ol"
      gridTemplateColumns={{ initial: '24px 1fr', md: '24px repeat(12, 1fr)' }}
      columnGap={columnGap}
      rowGap="2">
      {arrayChildren?.map((child, index) => {
        if (isElement(child)) {
          const interactiveProps =
            variant === 'interactive'
              ? {
                  isActive: Boolean(activeStepState && index + 1 === activeStepState),
                  setActiveStep,
                }
              : {};

          return cloneElement(child as ReactElement<StepListItemProps>, {
            amountOfSteps: arrayChildren.length,
            isInteractive: variant === 'interactive',
            step: `${index + 1}`,
            ...interactiveProps,
          });
        }
        return child;
      })}
    </Grid>
  );
};

StepList.Item = StepListItem;
